import { Activity, Apple, Brain, Smile, Trophy, Ruler, TrendingUp, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import AnimatedSection from "@/utils/AnimatedSection";

const tools = [
  { name: "Bone Age Prediction", description: "AI-powered bone age test from a single hand X-ray — skeletal maturity assessment in seconds", url: "/tools/bone-age/about", accent: "#4ECDC4", icon: Activity, internal: true },
  { name: "Growth Assessment", description: "Predict adult height, GHI score and IAP-2015 percentile position using Bayley-Pinneau and parental midpoint — no X-ray upload needed", url: "/tools/growth/about", accent: "#FFE66D", icon: Ruler, internal: true },
  { name: "NIN-NutriGuide", description: "Comprehensive nutrition analysis and dietary guidance based on ICMR-NIN 2020 standards", url: "/tools/nin-nutriguide/about", accent: "#FF6B6B", icon: Apple, internal: true },
  { name: "Young Athletes Program", description: "Specialised pediatric sports-medicine program for academies, schools and individual athletes — clinical care + diagnostic lab + AI monitoring", url: "/young-athletes", accent: "#A855F7", icon: Trophy, internal: true },
  { name: "Height Enhancement Program", description: "A personalised 4-step plan (growth assessment, blood tests, food plan and everyday habits) to help your child reach their full growth potential", url: "/tools/height-enhancement", accent: "#4ECDC4", icon: TrendingUp, internal: true },
  { name: "IQ PlayGround", description: "Engaging cognitive development assessments through interactive play modules", url: "https://iqplayground.in/", accent: "#A855F7", icon: Brain, internal: false },
  { name: "ToothLens AI", description: "AI-driven dental screening and oral health monitoring for early detection", url: "https://toothlensai.in/", accent: "#22D3EE", icon: Smile, internal: false }
];

const Tools = ({ theme }) => {
  return (
    <section id="tools-section" className="py-24 relative overflow-hidden bg-mesh" data-testid="tools-section">
      <div className="orb orb-teal w-[350px] h-[350px] -top-20 right-1/4" />
      <div className="orb orb-coral w-[300px] h-[300px] bottom-10 -left-20" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="badge-glass inline-flex px-4 py-1.5 rounded-full text-xs font-semibold text-[#4ECDC4] mb-5 uppercase tracking-wider">
              Our Technology
            </span>
            <h2 className="font-display text-4xl sm:text-5xl mb-4 text-white">
              Tools Powering tots360
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-white/60">
              Purpose-built AI platforms that drive each dimension of your child's health assessment
            </p>
          </div>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 gap-6">
          {tools.map((tool, i) => {
            const Icon = tool.icon;
            const Wrapper = tool.internal ? Link : 'a';
            const wrapperProps = tool.internal 
              ? { to: tool.url } 
              : { href: tool.url, target: "_blank", rel: "noopener noreferrer" };
            return (
              <AnimatedSection key={i} delay={i * 0.1}>
                <Wrapper
                  {...wrapperProps}
                  className="block glass-card p-8 group cursor-pointer"
                  data-testid={`tool-card-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${tool.accent}20` }}>
                      <Icon className="w-7 h-7" style={{ color: tool.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-display text-xl text-white">{tool.name}</h3>
                        {!tool.internal && <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: tool.accent }} />}
                      </div>
                      <p className="text-sm leading-relaxed text-white/50">{tool.description}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold mt-4 group-hover:gap-2 transition-all" style={{ color: tool.accent }}>
                        {tool.internal ? 'Learn More' : 'Visit Platform'} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Wrapper>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Tools;
